import React, { useState, useEffect, useRef } from 'react';
import BackupRestoreModal from '../BackupRestoreModal';
import { track } from '../../utils/analytics';
import { formatTimeAgo } from '../../utils/cloudBackup';
import './RestoreFoundPrompt.scss';

/**
 * "This computer has less than your backup" — the confirm sheet behind the
 * snapshot engine's shrink guard (`needs_confirmation`). Fires on a machine's
 * FIRST backup when the cloud's newest portable snapshot holds meaningfully
 * more playlists than the local DJ apps do — the sign-in-on-a-second-machine
 * moment, where silently backing up would make the emptier library the
 * newest version.
 *
 * Any code that runs snapshots (BackupSection's "Back up now", the auto-sync
 * hook) raises it by dispatching `mixgoat:backup-shrink-guard` with the
 * guard payload as detail — one sheet, one implementation, wherever the run
 * started. Shown at most once per session; "Back up anyway" re-runs with
 * allowShrink, after which the engine never asks again on this machine.
 *
 * Uses the rfp-* skin shared by the one-time backup prompts.
 */
function ShrinkGuardPrompt() {
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const [guard, setGuard] = useState(null);
    const [showRestore, setShowRestore] = useState(false);
    const shownRef = useRef(false); // once per session — a held-off decision must not nag

    useEffect(() => {
        if (!backupApi) return undefined;
        const onGuard = (e) => {
            if (shownRef.current || !e?.detail) return;
            shownRef.current = true;
            setGuard(e.detail);
            track('backup_shrink_guard_shown', {
                cloud_playlists: e.detail.cloud?.playlists || 0,
                local_playlists: e.detail.local?.playlists || 0,
            });
        };
        window.addEventListener('mixgoat:backup-shrink-guard', onGuard);
        return () => window.removeEventListener('mixgoat:backup-shrink-guard', onGuard);
    }, [backupApi]);

    const restoreFirst = () => {
        track('backup_shrink_guard_choice', { choice: 'restore_first' });
        setGuard(null);
        setShowRestore(true);
    };
    const backUpAnyway = async () => {
        track('backup_shrink_guard_choice', { choice: 'backup_anyway' });
        setGuard(null);
        try {
            // Mirror "Check now": the main process continues into the files
            // leg once the snapshot layer is through (`thenFiles`), in the
            // same backup pass — unless the user has it stopped or snoozed.
            await backupApi.runSnapshot({ force: true, allowShrink: true, thenFiles: true });
        } catch { /* the Backup tab reports run results */ }
    };
    const dismiss = () => {
        track('backup_shrink_guard_choice', { choice: 'not_now' });
        setGuard(null);
    };

    if (!backupApi) return null;

    const cloudLine = guard ? [
        `${(guard.cloud?.playlists || 0).toLocaleString()} playlists`,
        guard.cloudHostname ? `from ${guard.cloudHostname}` : null,
        guard.cloudAt ? formatTimeAgo(new Date(guard.cloudAt).getTime()) : null,
    ].filter(Boolean).join(' · ') : '';

    return (
        <>
            {guard && (
                <div className="rfp-overlay">
                    <div className="rfp-card" role="dialog" aria-label="This computer has less than your backup">
                        <div className="rfp-icon">
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 17.6A4.5 4.5 0 0 0 17.5 9.4a6 6 0 0 0-11.6 1.6A4 4 0 0 0 6 19h11" />
                                <path d="M12 18v-6M9.5 14.5L12 12l2.5 2.5" />
                            </svg>
                        </div>
                        <div className="rfp-title">This computer has less than your backup</div>
                        <div className="rfp-sub">
                            Your backup holds {cloudLine}. This computer has{' '}
                            {(guard.local?.playlists || 0).toLocaleString()} — restore before backing up from here?
                            Your existing backup versions are kept either way.
                        </div>
                        <div className="rfp-actions">
                            <button type="button" className="rfp-quiet-btn" onClick={dismiss}>Not now</button>
                            <button type="button" className="rfp-quiet-btn" onClick={backUpAnyway}>Back up anyway</button>
                            <button type="button" className="rfp-primary-btn" onClick={restoreFirst}>Restore first…</button>
                        </div>
                    </div>
                </div>
            )}
            <BackupRestoreModal show={showRestore} onHide={() => setShowRestore(false)} />
        </>
    );
}

export default ShrinkGuardPrompt;
